import { useContext } from 'react';
import styled from 'styled-components';
import PdpLocale from 'locale/en/pdp';
import { BodyXsBold, BodySmReg, TitleSmReg } from '@seed-health/tokens';
import { ProductsContext } from 'context-providers/products-provider';
import { formatMoney, formatMoneyWholeAware } from 'lib/utils/format-money';
import { applyShopifyPercentDiscount } from 'lib/utils/shopify-percent-discount';
import { BUNDLE_DISCOUNT_PERCENT } from 'lib/constants/bundle-discount';
import { getLocalizedPrice } from 'lib/utils/get-localized-price';
import { applyPromoCodeDiscount } from 'lib/utils/coupons/apply-promo-code-discount';
import { getMultiOrderCount } from 'lib/utils/coupons/get-multi-order-count';

const PriceWrapper = styled.div`
  margin-top: var(--spacing-16);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-8);
`;

const Prices = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: var(--spacing-8);
`;

const Price = styled.p`
  ${TitleSmReg};
  color: var(--fill-brand);
`;

const DiscountPrice = styled.p`
  ${TitleSmReg};
  color: var(--color-neutral-faded-green-60);
  text-decoration-line: line-through;
`;

const Tag = styled.span`
  border-radius: var(--radius-full);
  padding: var(--spacing-4) var(--spacing-8);
  color: var(--fill-brand);
  background-color: var(--guidance-primary);
  ${BodyXsBold};
`;

const Guarantee = styled.p`
  ${BodySmReg};
`;

const IntroPricing = styled.p`
  ${BodySmReg};
  opacity: 0.5;
  margin-top: var(--spacing-16);
  text-align: center;
`;

// Shopify variant amounts follow the shopper's market; the always-USD
// plans-API fields are only a fallback while variant data loads.
const getLocalizedAmounts = (product) => {
  const localizedPrice = getLocalizedPrice(product);
  return {
    price: parseFloat(localizedPrice),
    fullPrice: parseFloat(
      product?.shopifyData?.compareAtPrice?.amount ??
        localizedPrice ??
        product?.fullPrice,
    ),
  };
};

export const getProductPricing = ({
  selectedProduct,
  bundleProduct,
  promoCode,
  salePriceOverride,
  currencyCode,
}) => {
  const selectedAmounts = getLocalizedAmounts(selectedProduct);
  const bundleAmounts = bundleProduct
    ? getLocalizedAmounts(bundleProduct)
    : null;
  const bundleFullPrice = bundleAmounts ? bundleAmounts.fullPrice : 0;

  // Shopify discounts each line separately, so sum the discounted lines —
  // taking 75% of the combined total can come out a cent below checkout.
  const bundledPrice = bundleAmounts
    ? Number(
        applyShopifyPercentDiscount(
          selectedAmounts.price,
          BUNDLE_DISCOUNT_PERCENT,
        ),
      ) +
      Number(
        applyShopifyPercentDiscount(
          bundleAmounts.price,
          BUNDLE_DISCOUNT_PERCENT,
        ),
      )
    : selectedAmounts.price;

  const fullPrice = selectedAmounts.fullPrice + bundleFullPrice;
  const { price: computedPrice, discountTag } = applyPromoCodeDiscount(
    promoCode,
    bundledPrice,
    currencyCode,
  );

  // The resolved code wins once it lands — it's what checkout will charge, and
  // it may beat the sale. The server-rendered sale price stands in until then
  // (and if the discount lookup fails), so the price never flashes full. It's
  // computed against the market base price, so it holds in every currency.
  const price =
    discountTag || bundleProduct || salePriceOverride == null
      ? computedPrice
      : parseFloat(salePriceOverride);

  return { price, fullPrice, bundledPrice, discountTag };
};

export const ProductIntroPricing = ({
  selectedProduct,
  bundleProduct,
  promoCode,
  salePriceOverride = null,
}) => {
  const { currencyCode } = useContext(ProductsContext);
  const { price, bundledPrice, discountTag } = getProductPricing({
    selectedProduct,
    bundleProduct,
    promoCode,
    salePriceOverride,
    currencyCode,
  });
  const introOrderCount = discountTag ? getMultiOrderCount(promoCode) : null;
  if (!introOrderCount) return null;

  return (
    <IntroPricing data-testid='hero-intro-pricing'>
      {`${formatMoneyWholeAware(price, currencyCode)}/mo for your first ${introOrderCount} orders, then ${formatMoney(bundledPrice, currencyCode)}/mo.`}
    </IntroPricing>
  );
};

const ProductPrice = ({
  selectedProduct,
  bundleProduct,
  tag = null,
  promoCode,
  salePriceOverride = null,
  guaranteeHtml = PdpLocale.pdp_guarantee_v2,
}) => {
  const { currencyCode } = useContext(ProductsContext);
  const { price, fullPrice, discountTag } = getProductPricing({
    selectedProduct,
    bundleProduct,
    promoCode,
    salePriceOverride,
    currencyCode,
  });

  const hasDiscountPrice = fullPrice !== price;

  const getTagText = () => {
    if (discountTag) {
      return discountTag;
    } else if (bundleProduct) {
      return 'Bundle + Save 25%';
    } else {
      return tag;
    }
  };

  return (
    <PriceWrapper>
      {hasDiscountPrice && (tag || bundleProduct || discountTag) && (
        <Tag data-testid='hero-coupon-box'>{getTagText()}</Tag>
      )}
      <Prices>
        <Price data-testid={hasDiscountPrice ? 'hero-new-price' : 'hero-price'}>
          {formatMoney(price, currencyCode)}
        </Price>
        {hasDiscountPrice && (
          <DiscountPrice data-testid='hero-old-price'>
            {formatMoney(fullPrice, currencyCode)}
          </DiscountPrice>
        )}
        {!hasDiscountPrice && tag && <Tag>{tag}</Tag>}
      </Prices>
      <Guarantee dangerouslySetInnerHTML={{ __html: guaranteeHtml }} />
    </PriceWrapper>
  );
};

export default ProductPrice;
